import React, { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { l, Loader } from '@manychat/manyui'

import { useSelectedGoalData } from 'apps/aiAutomation/components/EngagementGoalEditor/hooks/useSelectedGoalData'
import { useGradientShift } from 'apps/aiAutomation/components/InstantAnswers/components/PreviewTab/hooks/useGradientShift'
import { Chat } from 'apps/aiAutomation/components/Playground/components/Chat/Chat'
import { MessageForm } from 'apps/aiAutomation/components/Playground/components/MessageForm/MessageForm'
import { ConversationStatus } from 'apps/aiAutomation/components/Playground/types'
import { useViewState } from 'apps/aiAutomation/hooks/layout'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  LeadQualPlaygroundActions,
  LeadQualPlaygroundSelectors,
} from 'apps/aiAutomation/store/leadQualPlayground'

import { ConversationFinishedControls } from './components/ConversationFinishedControls/ConversationFinishedControls'
import { EmptyState } from './components/EmptyState/EmptyState'

import cm from './LeadQualPlayground.module.css'

export const LeadQualPlayground = () => {
  const dispatch = useAppDispatch()

  const { selectedGoalId: goalId } = useSelectedGoalData()

  const sessionId = useAppSelector(LeadQualPlaygroundSelectors.getSessionId)
  const messages = useAppSelector(LeadQualPlaygroundSelectors.getMessages)
  const conversationStatus = useAppSelector(LeadQualPlaygroundSelectors.getConversationStatus)
  const isLoading = conversationStatus === ConversationStatus.ONGOING_WAITING
  const isIdleConversation = conversationStatus === ConversationStatus.IDLE

  const { isOpen } = useViewState(SidebarViewType.ENGAGEMENT_GOAL_EDITOR)

  const { gradientRef, messageFormRef } = useGradientShift({ isIdleConversation })

  const startSession = useCallback(() => {
    if (goalId) {
      dispatch(LeadQualPlaygroundActions.startSessionFx(goalId))
      AiAutomationAnalytics.logLeadQualPlaygroundStarted({ goalId, action: 'start' })
    }
  }, [dispatch, goalId])

  const restartSession = useCallback(() => {
    if (goalId) {
      dispatch(LeadQualPlaygroundActions.sessionReset())
      dispatch(LeadQualPlaygroundActions.startSessionFx(goalId))
      AiAutomationAnalytics.logLeadQualPlaygroundStarted({ goalId, action: 'force_restart' })
    }
  }, [dispatch, goalId])

  useEffect(() => {
    if (goalId && isOpen) {
      dispatch(AiAutomationLayoutActions.expandSidebarChanged(true))
    }
  }, [dispatch, goalId, isOpen])

  useEffect(() => {
    return () => {
      dispatch(LeadQualPlaygroundActions.sessionReset())
    }
  }, [dispatch])

  return (
    <article className={cm.previewWrapper}>
      <div ref={gradientRef} className={cm.background} />

      {isIdleConversation && <EmptyState onStart={startSession} />}

      <Chat
        messages={messages}
        conversationStatus={conversationStatus}
        ConversationFinishedControls={ConversationFinishedControls}
        onRestart={restartSession}
      />

      <div ref={messageFormRef} className="p-b p-x">
        {isLoading && (
          <div className={cm.loader}>
            <Loader className="m-0" size={16} />
          </div>
        )}

        <MessageForm
          placeholder={l.translate('Write a message...')}
          ariaLabel={l.translate('Message input for goal preview')}
          sessionId={sessionId}
          conversationStatus={conversationStatus}
          disabled={isIdleConversation}
          onSendMessage={(message) =>
            dispatch(LeadQualPlaygroundActions.sendMessageFx({ message }))
          }
        />
      </div>
    </article>
  )
}
